/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-indent-props */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Form, Input, Card, Button, Tag, Typography, Divider, Alert, Modal, AutoComplete } from 'antd';
import moment from 'moment';
import { debounce } from 'lodash';
import { useDocumentProcessing } from './DocumentProcessingContext';
import { useIntl } from 'react-intl';
import { Result } from '../types';
import { buildInitialFormData, getEmptyMultivalueRow, logMultivalueDebug, normalizeLineItemsSection, resolveMultivalueTupleSchema } from '../utils';
import { DatePicker, Select } from 'modules/form';
import {
  selectFieldsWithOptions,
  dateSchemas,
  selectFields,
  factorySelectFields,
  hideFormSectionsBasedOnType,
  conditionalFieldConfig,
  receiverNameSelectFields,
  nameToAddressFieldMap,
  nameToInitialAddressFieldMap,
} from './contants';
import EmailForm from './EmailForm';
import cloneDeep from 'lodash/cloneDeep';

const { Title, Text } = Typography;

type ReceiverNameSelectOption = { label: string; value: string; street: string | null; city: string | null; zipCode: string | null,  confidence: number,
  matchedBy: string,
  relevanceScore: number };

// Strip hyphenated initials (S-M-N) and space-separated initials (M V H), return remaining text for search
const stripInitialsForSearch = (s: string): string =>
  s
    .replace(/\b[A-Za-z](-[A-Za-z])+\b/g, '')   // S-M-N, A-B-C
    .replace(/\b[A-Za-z]( [A-Za-z])+\b/g, '')   // M V H, A B C
    .replace(/\s+/g, ' ')
    .trim();

// Normalize a string into match tokens, joining intra-word separators (e.g. S-M-N → smn)


const BUSINESS_STOP_WORDS = new Set([
  'gmbh', 'mbh', 'ag', 'kg', 'co', 'und', 'der', 'die', 'das',
  'hq', 'se', 'ug', 'ev', 'inc', 'ltd', 'eg', 'ohg', 'kag',
]);

function getBestMatch(initialValue: string, results: ReceiverNameSelectOption[], initialAddress?: string) {
  const normalize = (s: string) =>
    s.toLowerCase().replace(/-/g, ' ').replace(/[^\w\s]/g, '').trim();

  const query = normalize(initialValue);

  // 1️⃣ Exact match
  for (const result of results) {
    if (normalize(result.label) === query) {
      return result
    };
  }

  // 2️⃣ Extract distinctive words (strip common business suffixes/stop words)
  const allQueryWords = query.split(/\s+/).filter(Boolean);
  const distinctiveWords = allQueryWords.filter(w => !BUSINESS_STOP_WORDS.has(w) && w.length >= 3);
  if (distinctiveWords.length === 0) return null;

  // Extract primary name: the part before c/o or v.d. (ignore managed-by suffix)
  const primaryName = (label: string) =>
    normalize(label.split(/(?:c\/o|v\.d\.)/i)[0]);

  // Stem-aware word match:
  //   "verwaltungs" vs "verwaltung"     → prefix: longer.startsWith(shorter), min 4 chars
  //   "brahim"      vs "ibrahim"        → substring: rw.includes(queryWord), min 4 chars
  //   "aufzugtechnik" vs "aufzugstechnik" → long-word fuzzy: shared prefix ≥ 6, both words ≥ 8 chars
  const wordStemMatches = (primary: string, queryWord: string): boolean => {
    const resultWords = primary.split(/\s+/).filter(Boolean);
    return resultWords.some(rw => {
      if (rw === queryWord) return true;
      if (queryWord.length < 4) return false;
      const shorter = rw.length <= queryWord.length ? rw : queryWord;
      const longer = rw.length <= queryWord.length ? queryWord : rw;
      // prefix/stem match
      if (shorter.length >= 4 && longer.startsWith(shorter)) return true;
      // substring match: "brahim" inside "ibrahim"
      if (rw.includes(queryWord)) return true;
      // long-word fuzzy: "aufzugtechnik" ≈ "aufzugstechnik" (differ by inserted char)
      if (shorter.length >= 8) {
        let i = 0;
        while (i < shorter.length && i < longer.length && shorter[i] === longer[i]) i++;
        if (i >= 6) return true;
      }
      return false;
    });
  };

  // 3️⃣ Keep only results where ALL distinctive query words stem-match the primary name
  const matchingResults = results.filter(result =>
    distinctiveWords.every(qw => wordStemMatches(primaryName(result.label), qw))
  );

  if (matchingResults.length === 1) return matchingResults[0];

  // 4️⃣ Multiple matches → try to narrow by city extracted from initialAddress
  if (matchingResults.length > 1 && initialAddress) {
    const parts = initialAddress.split(',');
    const rawCity = parts[parts.length - 1].trim();
    // zipCode city → take only the city word(s) after the zip
    const cityOnly = rawCity.replace(/^\d+\s*/, '').trim().toLowerCase();
    if (cityOnly) {
      const byCity = matchingResults.filter(r => r.city?.toLowerCase() === cityOnly);
      if (byCity.length === 1) return byCity[0];
    }
  }

  return null;
}

type ReceiverNameSelectProps = {
  schemaId: string;
  initialValue?: string;
  initialAddress?: string;
  onMatchChange: (sid: string, isMatch: boolean) => void;
  onAutoPopulate: (_: ReceiverNameSelectOption) => void; // eslint-disable-line @typescript-eslint/no-unused-vars
  onSelect: (value: any, option: any) => void;
  /** Explicit value-change handler — not overridden by Form.Item */
  onChangeValue?: (value: any) => void;
  // injected by Form.Item
  value?: string;
  onChange?: (value: any) => void;
};

const ReceiverNameSelect = React.memo(({
  schemaId,
  initialValue,
  initialAddress,
  onMatchChange,
  onAutoPopulate,
  onSelect,
  onChangeValue,
  value,
  onChange,
}: ReceiverNameSelectProps) => {
  const { searchByName } = useDocumentProcessing();
  const intl = useIntl();
  const [options, setOptions] = useState<ReceiverNameSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const debouncedSearch = useMemo(
    () => debounce(async (query: string) => {
      if (!query) { setOptions([]); return; }
      setLoading(true);
      try {
        const results = await searchByName(query);
        if (!isMountedRef.current) return;
        setOptions(results);
      } catch {
        if (!isMountedRef.current) return;
        setOptions([]);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }, 300),
    [searchByName],
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!initialValue?.trim()) return;
    let cancelled = false;

    const splitByCo = initialValue.split(/(?:c\/o|vertr\.\s*d\.|v\.\s*d\.)\s*([^\n\r]+)/i);
    const firstLine = splitByCo[0].trim().replace('&', '');
    const coMatch = splitByCo[1]?.trim();

    // Any field with c/o or v.d. (e.g. "& Co. KG c/o ...", "Isleyen v.d. IPA GmbH") → search by entity after the marker
    // all others → search by first line only (before any marker)

    const rawQuery = schemaId?.split('.').pop() === 'name_invoice_receiver' && coMatch
      ? coMatch
      : firstLine;


    const searchQuery = stripInitialsForSearch(rawQuery);
    onChange?.(rawQuery);
    if (!searchQuery) {
    
      return
    };

    searchByName(searchQuery)
      .then((results: ReceiverNameSelectOption[]) => {
        if (cancelled || !isMountedRef.current) return;
        if (results.length > 0) {
          setOptions(results);

          const best = getBestMatch(searchQuery, results, initialAddress);
          if (best) {
            onMatchChange(schemaId, true);
            onChange?.(best.value);
            onChangeValue?.(best.value);
            onAutoPopulate(best);
          } else {
            onMatchChange(schemaId, false);
          }
        } else {
          onMatchChange(schemaId, false);
        }
      })
      .catch(() => {
        if (!cancelled && isMountedRef.current) {
          onMatchChange(schemaId, false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: check once on mount

  const handleSelect = useCallback(
    (value: any, option: any) => {
      onMatchChange(schemaId, true);
      onSelect(value, option);
    },
    [schemaId, onMatchChange, onSelect],
  );

  return (
    <AutoComplete
      value={value}
      onChange={onChange}
      options={options}
      style={{ width: '100%' }}
      filterOption={false}
      onSearch={debouncedSearch}
      notFoundContent={loading ? intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading…' }) : null}
      onSelect={handleSelect}
    />
  );
});

type FactorySelectProps = {
  schemaId: string;
  type: string;
  initialValue?: string;
  onMatchChange: (_sid: string, _isMatch: boolean) => void; // eslint-disable-line @typescript-eslint/no-unused-vars
  value?: string;
  onChange?: (value: any) => void;
};

const stripLeadingF = (val: string) => val.replace(/^F/i, '').trim();

const FactorySelect = ({ schemaId, type, initialValue, onMatchChange, value, onChange }: FactorySelectProps) => {
  const { searchByFactory } = useDocumentProcessing();
  const intl = useIntl();
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!initialValue?.trim()) {
      onMatchChange(schemaId, false);
      return;
    };
    let cancelled = false;
    searchByFactory(stripLeadingF(initialValue), type)
      .then(results => {
        if (cancelled || !isMountedRef.current) return;
        setOptions(results);
        if (results.length > 0) {
          const best = getBestMatch(initialValue, results as any);
          onMatchChange(schemaId, !!best);
        } else {
          onMatchChange(schemaId, false);
        }
      })
      .catch(() => {
        if (!cancelled && isMountedRef.current) {
          onMatchChange(schemaId, false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: check once on mount

  const debouncedSearch = useMemo(
    () => debounce(async (query: string) => {
      if (!query) { setOptions([]); return; }
      setLoading(true);
      try {
        const results = await searchByFactory(stripLeadingF(query), type);
        if (!isMountedRef.current) return;
        setOptions(results);
        if (results.length === 0) {
          onMatchChange(schemaId, false);
        }
      } catch {
        if (!isMountedRef.current) return;
        setOptions([]);
        onMatchChange(schemaId, false);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }, 300),
    [searchByFactory, type, schemaId, onMatchChange],
  );

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSelect = useCallback(() => {
    onMatchChange(schemaId, true);
  }, [schemaId, onMatchChange]);

  return (
    <AutoComplete
      value={value}
      onChange={onChange}
      options={options}
      style={{ width: '100%' }}
      filterOption={false}
      onSearch={debouncedSearch}
      onSelect={handleSelect}
      notFoundContent={loading ? intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading…' }) : null}
    />
  );
};

type SchemaItem = {
  category: 'section' | 'datapoint' | 'multivalue' | 'tuple' | 'field';
  schema_id?: string;
  children?: SchemaItem[];
  value?: any;
  type?: 'string' | 'enum' | 'number' | 'date';
  rir_confidence?: number | null;
  required?: boolean;
};

type PathSeg = string | number;

const DynamicForm = ({ schemaData, form }: { schemaData: Result; form: any }) => {
  const {
    setFormData,
    handleSubmitToSalesforce,
    submitToAufzugsHandwek,
    save,
    isSubmitting,
    openSendEmailModal,
    setOpenSendEmailModal,
    emailFrom,
    setEmailFrom,
    sendEmail,
    selectedDocument,
    tabKey,
  } = useDocumentProcessing();
  const intl = useIntl();
  const [initialValues, setInitialValues] = useState<any>(null);
  const [multiValues, setMultiValues] = useState<Record<string, any[]>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [nameMatchStatus, setNameMatchStatus] = useState<Record<string, boolean | null>>({});

  const handleMatchChange = useCallback((sid: string, isMatch: boolean) => {
    setNameMatchStatus(prev => ({ ...prev, [sid]: isMatch }));
  }, []);

  // Memoized debounced form data update
  const debouncedSetFormData = useMemo(() => debounce((values: any) => setFormData(values), 300), [setFormData]);

  useEffect(() => () => {
    debouncedSetFormData.cancel();
  }, [debouncedSetFormData]);

  // Memoized schema content
  const schemaContent = useMemo(
    () => normalizeLineItemsSection(schemaData?.content ?? []) as SchemaItem[],
    [schemaData],
  );

  const setFormValueAtPath = useCallback((path: PathSeg[], value: unknown) => {
    const allValues = cloneDeep(form.getFieldsValue(true));
    let current: Record<string, unknown> = allValues;

    path.forEach((key, index) => {
      if (index === path.length - 1) {
        current[key as string] = value;
        return;
      }

      const nextKey = path[index + 1];
      const existing = current[key as string];
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
        current[key as string] = typeof nextKey === 'number' ? [] : {};
      }
      current = current[key as string] as Record<string, unknown>;
    });

    form.setFieldsValue(allValues);
  }, [form]);

  // Memoized getNestedValue function
  const getNestedValue = useCallback((obj: any, path: PathSeg[]) => {
    return path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
  }, []);

  // Hydrate form with schema values as-is — no remapping or sanitizing on open
  useEffect(() => {
    if (schemaContent) {
      const init = buildInitialFormData(schemaContent as any);

      setInitialValues(init);
      form.setFieldsValue(init);
      setFormData(init);

      // Extract all multivalue arrays for local state management
      const extracted: Record<string, any[]> = {};
      const walk = (items: SchemaItem[], basePath: string[] = []) => {
        items.forEach(item => {
          if (item.category === 'multivalue' && item.schema_id) {
            const fullPath = [...basePath, item.schema_id].filter(Boolean).join('.');
            const arrValue = getNestedValue(init, [...basePath, item.schema_id]);
            extracted[fullPath] = Array.isArray(arrValue) ? arrValue : [];
          }
          if (item.children) {
            walk(item.children, item.schema_id ? [...basePath, item.schema_id] : basePath);
          }
        });
      };
      walk(schemaContent);
      setMultiValues(extracted);
    }
  }, [schemaContent, form, setFormData, getNestedValue]);

  // Memoized input renderer
  const renderInput = useCallback(
    (item: SchemaItem, basePath: PathSeg[] = []) => {
      const placeholder = `Enter ${item.schema_id?.replace(/_/g, ' ') || ''}`;

      if (item.schema_id === 'email_body') {
        return <div dangerouslySetInnerHTML={{ __html: item.value }} />;
      }

      if (item.schema_id === 'email_header_from' || item.schema_id === 'email_header_subject') {
        return <div>{item.value}</div>;
      }

      if (receiverNameSelectFields.includes(item.schema_id || '')) {
        const fieldKey = [...basePath, item.schema_id!].join('.');
        const addressMap = nameToAddressFieldMap[item.schema_id || ''];
        const applyAddress = (option: ReceiverNameSelectOption) => {
          if (!addressMap) return;
          const addressPath = [...basePath, addressMap.address];
          if (form.getFieldValue(addressPath) === undefined) return;
          const { street, zipCode, city } = option;
          const cityPart = [zipCode, city].filter(Boolean).join(' ');
          const combined = [street, cityPart].filter(Boolean).join(', ') || null;
          form.setFields([{ name: addressPath, value: combined }]);
          debouncedSetFormData(form.getFieldsValue(true));
        };
        const initialAddressField = nameToInitialAddressFieldMap[item.schema_id || ''];
        const initialAddress = initialAddressField
          ? form.getFieldValue([...basePath, initialAddressField]) ?? undefined
          : undefined;
        return (
          <ReceiverNameSelect
            schemaId={fieldKey}
            initialValue={typeof item.value === 'string' ? item.value : undefined}
            initialAddress={typeof initialAddress === 'string' ? initialAddress : undefined}
            onMatchChange={handleMatchChange}
            onAutoPopulate={(firstResult) => {
              applyAddress(firstResult);
            }}
            onSelect={(_value: any, option: any) => applyAddress(option)}
            onChangeValue={(val) => {
              form.setFields([{ name: [...basePath, item.schema_id!], value: val }]);
              debouncedSetFormData(form.getFieldsValue(true));
            }}
          />
        );
      }

      const factoryType = factorySelectFields[item.schema_id?.toLowerCase() || ''];
      if (factoryType) {
        const fieldKey = [...basePath, item.schema_id!].join('.');
        const fieldPath = [...basePath, item.schema_id!];
        const formValue = form.getFieldValue(fieldPath);
        const initialValue = typeof formValue === 'string' && formValue
          ? stripLeadingF(formValue)
          : formValue;
        return <FactorySelect schemaId={fieldKey} type={factoryType} initialValue={initialValue} onMatchChange={handleMatchChange} />;
      }

      if (selectFields.includes(item.schema_id?.toLowerCase() || '')) {
        const isResponseTimesType =
          basePath[0] === 'response_times_section' && item.schema_id?.toLowerCase() === 'type';
        const selectKey = isResponseTimesType ? 'response_times_type' : item.schema_id?.trim().toLowerCase() || '';

        return (
          <Select
            options={selectFieldsWithOptions(intl, selectKey) || []}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      }

      switch (item.schema_id) {
        case 'due_date_invoice':
        case 'due_date_discount':
          return <DatePicker style={{ width: '100%' }} />;
      }

      switch (item.type) {
        case 'string':
        case 'enum':
          return <Input placeholder={placeholder} />;
        case 'number':
          return <Input type="number" style={{ width: '100%' }} />;
        case 'date':
          return <DatePicker style={{ width: '100%' }} />;
        default:
          return <Input placeholder={placeholder} />;
      }
    },
    [intl, form, debouncedSetFormData, handleMatchChange],
  );

  // Memoized form elements renderer
  const renderFormElements = useCallback(
    (elements: SchemaItem[], basePath: PathSeg[] = []) => {
      return elements.map((element: SchemaItem, index: number) => {
        const label = element.schema_id ? intl.formatMessage({ id: element.schema_id?.trim() }) : '';
        const formValues = form.getFieldsValue(true);
        // ===== CONDITIONAL FIELD HIDING =====
        if (element.schema_id && conditionalFieldConfig[element.schema_id]) {
          const config = conditionalFieldConfig[element.schema_id];
          const sourceValue = getNestedValue(formValues, config.dependsOn.split('.'));

          if (config.hideWhen.includes(sourceValue)) {
            return null; // Completely hide the field
          }
        }

        if (element.schema_id === 'email_info_section' && selectedDocument.type === 'Landing_Page_Assessment_Report') {
          return null;
        }

        // Section
        if (element.category === 'section') {
          const sectionKey = element.schema_id || `section_${index}`;
          const nextPath = [...basePath, element.schema_id || sectionKey];

          // if schema_id = email_information add a button with title that set openSendEmailModal to true with title props
          const emailFrom = element.children?.find(c => c.schema_id === 'email_header_from')?.value;
          return (
            <Card
              key={sectionKey}
              title={
                element.schema_id === 'email_info_section' && emailFrom ? (
                  <>
                    <Title level={4} style={{ whiteSpace: 'normal', wordBreak: 'break-word', margin: 0 }}>{label}</Title>
                    <Button
                      type="link"
                      onClick={() => {
                        setOpenSendEmailModal(true);
                        setEmailFrom(emailFrom);
                      }}
                    >
                      {intl.formatMessage({ id: 'email.send.title' })}
                    </Button>
                  </>
                ) : (
                  <Title level={4} style={{ whiteSpace: 'normal', wordBreak: 'break-word', margin: 0 }}>{label}</Title>
                )
              }
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#fafafa' }}
            >
              {element.children ? renderFormElements(element.children, nextPath) : null}
            </Card>
          );
        }

        // Datapoint / Field
        if (
          (element.category === 'datapoint' || element.category === 'field') &&
          !hideFormSectionsBasedOnType(selectedDocument.type || '').includes(element.schema_id || '')
        ) {
          const suppressRequired = element.schema_id === 'item_deficiency_id' &&
            form.getFieldValue([...basePath, 'item_result_pick']) === 'unclassified_deficiency';
          const isReq = !!element.required && !suppressRequired;
          const namePath: PathSeg[] = [...basePath, element.schema_id || `${element.category}_${index}`];
          const fieldKey = namePath.join('.');

          return (
            <Form.Item
              key={fieldKey}
              name={namePath}
              {...(suppressRequired ? { validateStatus: '', help: '' } : {})}
              label={
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {label}
                  {element.rir_confidence != null && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {intl.formatMessage({ id: 'document.processing.form.confidence' })} :{' '}
                      {(element.rir_confidence * 100).toFixed(1)}%
                    </Tag>
                  )}
                  {nameMatchStatus[fieldKey] === true ? (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      {intl.formatMessage({ id: 'document.processing.form.match', defaultMessage: 'Match' })}
                    </Tag>
                  ) : nameMatchStatus[fieldKey] === false ? (
                    <Tag color="red" style={{ marginLeft: 8 }}>
                      {intl.formatMessage({ id: 'document.processing.form.notMatch', defaultMessage: 'Not Match' })}
                    </Tag>
                  ) : null}
                </span>
              }
              rules={
                isReq
                  ? [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'document.processing.form.requiredField',
                          defaultMessage: 'This field is required',
                        }),
                      },
                    ]
                  : []
              }
                      {...(element.type === 'date' || dateSchemas.includes(element.schema_id)
                ? {
                    getValueProps: (val?: string) => {
                      if (!val) return { value: null };
                      const dateMoment = moment(val);
                      return { value: dateMoment.isValid() ? dateMoment : null };
                    },
                    getValueFromEvent: (_: any, dateString: string) => dateString || null,
                  }
                : {})}
            >
              {renderInput(element, basePath)}
            </Form.Item>
          );
        }

        // Multivalue (manual, no Form.List)
        if (element.category === 'multivalue') {
          const listName: PathSeg[] = [...basePath, element.schema_id!];
          const fullPathKey = listName.join('.');
          const liveTuple =
            element.children?.find(c => c.category === 'tuple')
            ?? (element as SchemaItem & { _tupleTemplate?: SchemaItem })._tupleTemplate
            ?? null;
          const tupleSchema = resolveMultivalueTupleSchema(
            fullPathKey,
            selectedDocument?.type,
            liveTuple as any,
          );
          const valuesArray = multiValues[fullPathKey] || [];

          const applyFieldArray = (nextArr: any[]) => {
            setMultiValues(prev => ({ ...prev, [fullPathKey]: nextArr }));
            setFormValueAtPath(listName, nextArr);
          };

          const getCurrentArrayFromForm = (): any[] => {
            // prefer the live form value, fall back to state if undefined
            const live = form.getFieldValue(listName);
            return Array.isArray(live) ? live : multiValues[fullPathKey] || [];
          };

          const onRemove = (idx: number) => {
            const current = cloneDeep(getCurrentArrayFromForm());
            const next = current.filter((_, i) => i !== idx);
            applyFieldArray(next); // can be []
          };

          const onAdd = () => {
            const current = cloneDeep(getCurrentArrayFromForm());
            const blank = getEmptyMultivalueRow(fullPathKey, selectedDocument?.type, liveTuple as any);
            const next = [...current, blank];

            logMultivalueDebug('onAdd', {
              fullPathKey,
              listName,
              salesforceType: selectedDocument?.type,
              multivalueSchemaId: element.schema_id,
              hasLiveTuple: !!liveTuple,
              hasTupleTemplate: !!(element as SchemaItem & { _tupleTemplate?: SchemaItem })._tupleTemplate,
              tupleChildCount: element.children?.length ?? 0,
              tupleSchemaFieldIds: tupleSchema?.children?.map(child => child.schema_id).filter(Boolean),
              blankRowKeys: Object.keys(blank),
              blank,
              currentLength: current.length,
              nextLength: next.length,
            });

            applyFieldArray(next);
          };
          return (
            <div key={fullPathKey} style={{ marginBottom: 24 }}>
              <Divider orientation="left">
                <Text strong>{label}</Text>
              </Divider>

              {valuesArray.length === 0 && (
                <Alert
                  type="info"
                  showIcon
                  message={intl.formatMessage({
                    id: 'document.processing.form.noItems',
                    defaultMessage: 'No items yet.',
                  })}
                  style={{ marginBottom: 12 }}
                />
              )}

              {valuesArray.map((_, idx) => {
                const rowBasePath = [...listName, idx];
                const itemKey = `${fullPathKey}-${idx}`;
                const deficiencyId = form.getFieldValue([...rowBasePath, 'item_deficiency_id']);
                const titleSuffix = deficiencyId ? ` (${deficiencyId})` : '';

                return (
                  <Card
                    key={itemKey}
                    style={{
                      marginBottom: 20,
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      background: '#f9fafc',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      position: 'relative',
                    }}
                    headStyle={{
                      background: '#f0f5ff',
                      borderBottom: '1px solid #d9d9d9',
                    }}
                    title={
                      <Text strong style={{ fontSize: 14 }}>
                        {intl.formatMessage({ id: 'document.processing.form.lineItem' })} #{idx + 1}{titleSuffix}
                      </Text>
                    }
                    bodyStyle={{ padding: '16px 16px 8px 16px' }}
                  >
                    {tupleSchema ? renderFormElements((tupleSchema.children || []) as SchemaItem[], rowBasePath) : null}

                    {/* Always allow removal, even if it's the last one */}
                    <Button
                      danger
                      type="link"
                      onClick={() => onRemove(idx)}
                      style={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      {intl.formatMessage({ id: 'document.processing.form.removeItem' })}
                    </Button>
                  </Card>
                );
              })}

              <Button type="dashed" onClick={onAdd} block>
                {intl.formatMessage({ id: 'document.processing.form.addItem' })}
              </Button>
            </div>
          );
        }

        // Tuple
        if (element.category === 'tuple') {
          const tupleKey = element.schema_id || `tuple_${index}`;
          return (
            <React.Fragment key={tupleKey}>
              {element.children ? renderFormElements(element.children, basePath) : null}
            </React.Fragment>
          );
        }

        return null;
      });
    },
    [intl, selectedDocument.type, getNestedValue, setFormValueAtPath, setOpenSendEmailModal, setEmailFrom, renderInput, multiValues, form, nameMatchStatus],
  );

  // Stable form key
  const formKey = useMemo(() => `form-${schemaData?.arrived_at || 'default'}`, [schemaData]);

  const hideFormActions = tabKey === '7';

  if (!initialValues) return null;

  return (
    <div style={{ padding: '0 24px', height: 'calc(100vh - 56px)', overflowY: 'auto' }}>
      {errorMessage && (
        <Alert
          message={errorMessage}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMessage(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        key={formKey}
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={(_, allValues) => {
          debouncedSetFormData(allValues);
        }}
        onFinish={values => {
          if (hideFormActions) return;
          debouncedSetFormData.cancel();
          setFormData(values);
          if (selectedDocument.type === 'Landing_Page_Assessment_Report') {
            submitToAufzugsHandwek(values);
          } else {
            handleSubmitToSalesforce(values);
          }
        }}
        onFinishFailed={({ errorFields }) => {
          setErrorMessage(
            intl.formatMessage({
              id: 'document.processing.form.validationError',
              defaultMessage: 'Please correct the highlighted errors before submitting.',
            }),
          );
          window.scrollTo({ top: 0, behavior: 'smooth' });
          form.scrollToField(errorFields[0].name);
        }}
      >
        {renderFormElements(schemaContent)}
        {!hideFormActions && (
        <Form.Item>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="default"
              onClick={async () => {
                try {
                  const values = await form.validateFields();
                  debouncedSetFormData.cancel();
                  setFormData(values);
                  await save(values);
                } catch (error) {
                  // Validation failed
                }
              }}
              disabled={isSubmitting}
              style={{ flex: 1 }}
            >
              {intl.formatMessage({ id: 'document.processing.action.save', defaultMessage: 'Save' })}
            </Button>
            <Button type="primary" htmlType="submit" style={{ flex: 1 }} disabled={isSubmitting} loading={isSubmitting}>
              {intl.formatMessage({
                id: selectedDocument.type === 'Landing_Page_Assessment_Report'
                  ? 'document.processing.action.submitToAI'
                  : 'document.processing.action.submitToSalesforce',
              })}
            </Button>
          </div>
        </Form.Item>
        )}
      </Form>
      <EmailForm
        openSendEmailModal={openSendEmailModal}
        setOpenSendEmailModal={setOpenSendEmailModal}
        onSendEmail={sendEmail}
        emailFrom={emailFrom}
      />
    </div>
  );
};

export default React.memo(DynamicForm);
